import React from 'react';
import PropTypes from 'prop-types';
import { Input, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import INITIAL_VALUE from 'constants/initialValue';
import images from 'assets/images';

import 'react-lazy-load-image-component/src/effects/blur.css';
import styles from './CardPicture.module.scss';

const stopDragEvent = (e) => {
  e.stopPropagation();
};

function CardPicture({
  isLoading,

  roomId,
  previewImage,
  roomPreviewImage,
  picture,
  pictureSelected,

  onUpdatePlacePreviewImage,
  onSetRoomPreviewImage,
  onCheckboxChange,
}) {
  const [isPreviewOpen, setIsPreviewOpen] = React.useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id: picture.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleCheckboxChange = React.useCallback((e) => {
    e.stopPropagation();
    onCheckboxChange(picture, e.target.checked);
  }, [onCheckboxChange, picture]);

  const { isVideo } = picture;
  const mediaSrc = picture.image || picture;

  const onUpdatePreviewImage = React.useCallback(
    (image, thisRoomId) => (roomPreviewImage
      ? () => onSetRoomPreviewImage(image, thisRoomId)
      : () => onUpdatePlacePreviewImage(image)
    ),
    [onSetRoomPreviewImage, onUpdatePlacePreviewImage, roomPreviewImage],
  );

  const handleOpenPreview = React.useCallback((e) => {
    stopDragEvent(e);
    setIsPreviewOpen(true);
  }, []);

  const handleClosePreview = React.useCallback(() => {
    setIsPreviewOpen(false);
  }, []);

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className={`${styles.Card} ${pictureSelected.includes(picture) && styles.Active}`}
      >
        <div className={styles.Overlay}>
          <Input
            id={picture.id || picture}
            type="checkbox"
            checked={pictureSelected.includes(picture)}
            className={styles.Checkbox}
            value={picture.id || picture}
            onChange={handleCheckboxChange}
            onClick={stopDragEvent}
            onPointerDown={stopDragEvent}
            onMouseDown={stopDragEvent}
            onTouchStart={stopDragEvent}
          />
          {(roomPreviewImage || previewImage) === (picture.image || picture) ? (
            <div className={`${styles.ChooseAvatar} ${styles.Active}`}>
              <i className="bx bx-star" />
              <span className={styles.ChooseAvatarText}>Ảnh đại diện</span>
            </div>
          ) : !isVideo && (
            <button
              type="button"
              className={styles.ChooseAvatar}
              onClick={onUpdatePreviewImage(mediaSrc, roomId)}
              disabled={isLoading}
              onPointerDown={stopDragEvent}
              onMouseDown={stopDragEvent}
              onTouchStart={stopDragEvent}
            >
              <i className="bx bx-star" />

              <span className={styles.ChooseAvatarText}>Chọn làm ảnh đại diện</span>
            </button>
          )}

          <button
            type="button"
            className={styles.BtnZoom}
            onClick={handleOpenPreview}
          >
            <img src={images.controlCameraIcon.default} alt="icon" className={styles.BtnZoomIcon} />
            <span className={styles.BtnZoomContent}>{isVideo ? 'Xem video' : 'Xem'}</span>
          </button>
        </div>

        {isVideo ? (
          <div className={styles.ImageWrap}>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video
              src={mediaSrc}
              className={styles.Image}
              muted
              playsInline
              preload="metadata"
            />
            <div className={styles.VideoPlayIcon}>
              <i className="bx bx-play-circle" />
            </div>
          </div>
        ) : (
          <LazyLoadImage
            alt="preview"
            effect="blur"
            src={mediaSrc}
            wrapperClassName={styles.ImageWrap}
            className={styles.Image}
          />
        )}
      </div>

      <Modal isOpen={isPreviewOpen} toggle={handleClosePreview} size="lg" centered>
        <ModalHeader toggle={handleClosePreview} className="border-0 pb-0" />
        <ModalBody className="text-center p-3 overflow-hidden d-flex justify-content-center align-items-center">
          {isVideo ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video
              src={mediaSrc}
              controls
              autoPlay
              playsInline
              style={{
                maxWidth: '100%',
                maxHeight: '75vh',
                borderRadius: '4px',
              }}
            />
          ) : (
            <img
              src={mediaSrc}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '75vh',
                objectFit: 'contain',
                borderRadius: '4px',
              }}
            />
          )}
        </ModalBody>
      </Modal>
    </>
  );
}

CardPicture.propTypes = {
  isLoading: PropTypes.bool,

  roomId: PropTypes.string,
  previewImage: PropTypes.string,
  roomPreviewImage: PropTypes.string,
  picture: PropTypes.instanceOf(Object).isRequired,
  pictureSelected: PropTypes.instanceOf(Array),

  onSetRoomPreviewImage: PropTypes.func,
  onUpdatePlacePreviewImage: PropTypes.func,
  onCheckboxChange: PropTypes.func,
};

CardPicture.defaultProps = {
  isLoading: false,

  roomId: '',
  roomPreviewImage: '',
  previewImage: '',
  pictureSelected: INITIAL_VALUE.ARRAY,

  onSetRoomPreviewImage: INITIAL_VALUE.FUNCTION,
  onUpdatePlacePreviewImage: INITIAL_VALUE.FUNCTION,
  onCheckboxChange: INITIAL_VALUE.FUNCTION,
};

export default React.memo(CardPicture);
